import { z } from "zod";
import { MediaSchema } from "./media";
import { PartnerBadgeSchema, PublicPartnerBadgeSchema } from "./badge";
import { PublicServiceCategorySchema } from "./service-categories";

export const CustomerStoryStatusSchema = z.enum(["draft", "published"]);

export const CustomerStoryIndustrySchema = z.object({
    id: z.number(),
    slug: z.string(),
    label: z.string(),
    sort_index: z.number(),
});

export const CustomerStoryMetricDefinitionSchema = z.object({
    label: z.string(),
    prefix: z.string().nullable().optional(),
    suffix: z.string().nullable().optional(),
});

export const CustomerStoryMetricSchema = CustomerStoryMetricDefinitionSchema.extend({
    before_value: z.string().nullable().optional(),
    after_value: z.string().nullable().optional(),
});

export const CustomerStoryTextFieldHandleSchema = z.enum([
    "about_the_client",
    "target_customers",
    "project_goals",
    "problem_before_the_project",
    "project_overview",
    "key_improvements_implemented",
    "result_summary",
    "client_testimonial",
]);

export const CustomerStoryTextFieldSchema = z.object({
    handle: CustomerStoryTextFieldHandleSchema,
    value: z.string().nullable().optional(),
});

export const CustomerStoryGalleryMediaInputSchema = z.object({
    id: z.string(),
    sort_order: z.number(),
});

export const CustomerStoryCardSchema = z.object({
    id: z.number(),
    title: z.string(),
    slug: z.string(),
    status: CustomerStoryStatusSchema,
    thumbnail: z.string().url().nullable().optional(),
    thumbnail_media: MediaSchema.nullable().optional(),
    gallery_images: z.array(z.string().url()).optional().default([]),
    gallery_media: z.array(MediaSchema).optional().default([]),
    hired_at_shopexperts: z.boolean().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    period: z.string().nullable().optional(),
    brand_name: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    industry: CustomerStoryIndustrySchema.nullable().optional(),
    shopify_plan: z.enum(["basic", "grow", "advanced", "plus"]).nullable().optional(),
    client_name: z.string().nullable().optional(),
    client_role: z.string().nullable().optional(),
    service_subcategories: z.array(PublicServiceCategorySchema).optional().default([]),
    badges: z.array(z.union([PartnerBadgeSchema, PublicPartnerBadgeSchema])).optional().default([]),
    text_fields: z.array(CustomerStoryTextFieldSchema).optional().default([]),
    metrics: z.array(CustomerStoryMetricSchema).optional().default([]),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
});

export const ExpertCustomerStoryDetailsSchema = CustomerStoryCardSchema;

export const CustomerStoryUpsertRequestSchema = z.object({
    title: z.string(),
    slug: z.string().nullable().optional(),
    status: CustomerStoryStatusSchema,
    brand_name: z.string().nullable().optional(),
    website: z.string().nullable().optional(),
    industry: z.string().nullable().optional(),
    shopify_plan: z.enum(["basic", "grow", "advanced", "plus"]).nullable().optional(),
    client_name: z.string().nullable().optional(),
    client_role: z.string().nullable().optional(),
    thumbnail_media_id: z.string().nullable().optional(),
    gallery_media: z.array(CustomerStoryGalleryMediaInputSchema).nullable().optional(),
    badge_ids: z.array(z.number()).optional(),
    service_subcategory_ids: z.array(z.number()).optional(),
    text_fields: z.array(CustomerStoryTextFieldSchema).optional(),
    metrics: z.array(CustomerStoryMetricSchema).optional(),
    hired_at_shopexperts: z.boolean().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
});

export type CustomerStoryStatus = z.infer<typeof CustomerStoryStatusSchema>;
export type CustomerStoryIndustry = z.infer<typeof CustomerStoryIndustrySchema>;
export type CustomerStoryMetricDefinition = z.infer<typeof CustomerStoryMetricDefinitionSchema>;
export type CustomerStoryMetric = z.infer<typeof CustomerStoryMetricSchema>;
export type CustomerStoryTextFieldHandle = z.infer<typeof CustomerStoryTextFieldHandleSchema>;
export type CustomerStoryTextField = z.infer<typeof CustomerStoryTextFieldSchema>;
export type CustomerStoryGalleryMediaInput = z.infer<typeof CustomerStoryGalleryMediaInputSchema>;
export type CustomerStoryCard = z.infer<typeof CustomerStoryCardSchema>;
export type ExpertCustomerStoryDetails = z.infer<typeof ExpertCustomerStoryDetailsSchema>;
export type CustomerStoryUpsertRequest = z.infer<typeof CustomerStoryUpsertRequestSchema>;
